import ToolTipIcon from '@assets/images/common/icon/tooltip/tooltip.svg';
import { useToast } from '@features/toast/context/toastContext.tsx';
import TOAST_MESSAGE from '@modules/constants/toastMessage.ts';
import { handleApiResponse } from '@src/api/apiClient/axiosClient.ts';
import { tenantRoleApiClient } from '@src/api/tenant/common/role/tenantRoleApiClient.ts';
import {
  SuggestEmailErrorResponse,
  tenantUserApiClient,
  TenantUserShowResponse,
  TenantUserUpdateErrorResponse,
  TenantUserUpdateRequest,
} from '@src/api/tenant/tenantUser/tenantUserApiClient';
import { tenantUserResendInvitationApiClient } from '@src/api/tenant/tenantUser/tenantUserResendInvitationApiClient';
import SpinnerOverlay from '@src/components/SpinnerOverlay';
import { fetchTenantUserProfile } from '@src/context/auth/ProfileFetcher';
import { useTenantAuth } from '@src/context/auth/useTenantAuth';
import { EnableStatus } from '@src/enum/EnableStatus.ts';
import { useScrollToError } from '@src/hooks/useScrollToError';
import { TENANT_URL } from '@src/routes/tenant';
import Button from '@src/ui/elements/button/Button';
import { ErrorMessage } from '@src/ui/elements/form';
import Input from '@src/ui/elements/form/Input';
import Select, { SelectorItem } from '@src/ui/elements/form/Select';
import TenantRoleTooltip from '@src/ui/elements/form/TenantRoleTooltip';
import Title from '@src/ui/elements/typography/Title.tsx';
import SuggestEmailUpdateModal from '@src/ui/templates/Shared/SuggestEmailUpdateModal.tsx';
import { firstError, hasError } from '@src/utils/errorMessageUtils.ts';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

type FormValues = {
  familyName: string;
  firstName: string;
  familyNameKana: string;
  firstNameKana: string;
  email: string;
  roleId: string;
  enabled: string;
};

type RoleSelector = SelectorItem[];
type StatusSelector = SelectorItem[];

export type SuggestFormValues = {
  suggestedEmail: string;
};

const TenantUserEditForm: React.FC = () => {
  const navigate = useNavigate();
  const { profile, setProfile } = useTenantAuth();
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const helpIconRef = React.createRef<HTMLDivElement>();
  const [tenantUserShowResponse, setTenantUserShowResponse] =
    useState<TenantUserShowResponse | null>(null);
  const [formValues, setFormValues] = useState<FormValues | null>(null);
  const [errors, setErrors] = useState<TenantUserUpdateErrorResponse>();
  const [suggestValues, setSuggestValues] = useState<SuggestFormValues>({
    suggestedEmail: '',
  });
  const [suggestErrors, setSuggestErrors] = useState<SuggestEmailErrorResponse>();
  const [roleSelector, setRoleSelector] = useState<RoleSelector>();
  const [statusSelector, setStatusSelector] = useState<StatusSelector>();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [resendInvitationDisabled, setResendInvitationDisabled] = useState<boolean>(false);
  const [isOpenSuggestModal, setIsOpenSuggestModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [isTooltipActive, setIsTooltipActive] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const RESUBMIT_WAIT_TIME = 60 * 1000;

  const TOOLTIP_OFFSET_X = 10;
  const TOOLTIP_OFFSET_Y = 90;
  const TOOLTIP_ADJUST_Y = 150;
  const TOOLTIP_HEIGHT_THRESHOLD = 100;

  useEffect(() => {
    const fetchData = async () => {
      const res = await tenantUserApiClient.show(Number(id));
      handleApiResponse(res, async (data) => {
        setTenantUserShowResponse(data);

        const initialFormValues: FormValues = {
          familyName: data.family_name,
          firstName: data.first_name,
          familyNameKana: data.family_name_kana,
          firstNameKana: data.first_name_kana,
          email: data.email,
          roleId: String(data.role_id),
          enabled: data.enabled ? 'ENABLED' : 'DISABLED',
        };
        setFormValues(initialFormValues);
      });
    };
    fetchData();

    const fetchTenantRoles = async () => {
      const res = await tenantRoleApiClient.index();
      handleApiResponse(res, (data) => {
        setRoleSelector(
          data.map((role) => {
            return {
              id: String(role.id),
              name: role.name,
            };
          }),
        );
      });
    };
    fetchTenantRoles();

    setStatusSelector([
      { id: 'ENABLED', name: EnableStatus.ENABLED },
      { id: 'DISABLED', name: EnableStatus.DISABLED },
    ]);
  }, [id]);

  const submit = async () => {
    if (!formValues || submitted) {
      return;
    }
    setErrors(undefined);
    setSubmitted(true);

    const params: TenantUserUpdateRequest = {
      tenant_user: {
        family_name: formValues.familyName,
        first_name: formValues.firstName,
        family_name_kana: formValues.familyNameKana,
        first_name_kana: formValues.firstNameKana,
        role_id: formValues.roleId,
        expires_at: tenantUserShowResponse?.expires_at || undefined,
        enabled: formValues?.enabled ? formValues?.enabled === 'ENABLED' : undefined,
      },
    };
    const response = await tenantUserApiClient.update(Number(id), params);

    handleApiResponse(
      response,
      async () => {
        toast(TOAST_MESSAGE.UPDATE);
        // 自分自身を更新した場合、認証情報を再取得
        if (profile?.user?.id && Number(id) === profile.user.id) {
          await fetchTenantUserProfile(setProfile);
        }
        navigate(TENANT_URL.TENANT_USERS);
      },
      (errors) => {
        setErrors(errors);
        setSubmitted(false);
      },
    );
  };

  const handleClickCancel = () => {
    navigate(TENANT_URL.TENANT_USERS);
  };

  const handleClickResendInvitation = async () => {
    if (!tenantUserShowResponse || resendInvitationDisabled) {
      return;
    }
    setResendInvitationDisabled(true);

    const response = await tenantUserResendInvitationApiClient.resendInvitation(Number(id), {});
    handleApiResponse(response, () => {
      toast(TOAST_MESSAGE.RESEND);
    });

    setTimeout(() => {
      setResendInvitationDisabled(false);
    }, RESUBMIT_WAIT_TIME);
  };

  const handleClickSuggest = () => {
    setSuggestErrors(undefined);
    setIsOpenSuggestModal(true);
  };

  const handleSuggestValuesChange = (newValues: Partial<SuggestFormValues>) => {
    setSuggestValues((prev) => ({ ...prev, ...newValues }));
  };

  const handleCancelSuggest = () => {
    setIsOpenSuggestModal(false);
    setSuggestValues({ suggestedEmail: '' });
  };

  const onSuggest = async () => {
    setLoading(true);
    setSuggestErrors(undefined);

    if (!tenantUserShowResponse) {
      return;
    }

    const res = await tenantUserApiClient.sendSuggestion(Number(tenantUserShowResponse.id), {
      family_name: tenantUserShowResponse.family_name,
      first_name: tenantUserShowResponse.first_name,
      suggested_email: suggestValues.suggestedEmail,
    });

    handleApiResponse(
      res,
      () => {
        setIsOpenSuggestModal(false);
        setSuggestValues({ suggestedEmail: '' });
        toast(TOAST_MESSAGE.SEND);
      },
      (errors) => {
        setSuggestErrors(errors);
      },
    );
    setLoading(false);
  };

  const fieldRefs = useScrollToError(errors ?? {});

  const calculateBasePosition = (rect: DOMRect) => ({
    x: rect.left + window.scrollX + rect.width + TOOLTIP_OFFSET_X,
    y: rect.top + window.scrollY - TOOLTIP_OFFSET_Y,
  });

  const adjustTooltipPosition = (basePosition: { x: number; y: number }, rect: DOMRect) => {
    if (rect.bottom + TOOLTIP_HEIGHT_THRESHOLD > window.innerHeight) {
      return {
        x: basePosition.x,
        y: basePosition.y - TOOLTIP_ADJUST_Y,
      };
    }
    return basePosition;
  };

  const handleTooltipPosition = () => {
    if (!helpIconRef.current) {
      return;
    }
    const rect = helpIconRef.current.getBoundingClientRect();
    const basePosition = calculateBasePosition(rect);
    const adjustedPosition = adjustTooltipPosition(basePosition, rect);
    setTooltipPosition(adjustedPosition);
  };

  const handleMouseOver = () => {
    setIsTooltipActive(true);
    handleTooltipPosition();
  };

  const handleMouseLeave = () => {
    setIsTooltipActive(false);
  };

  return (
    <main className='px-6 pb-12 pt-2'>
      <SpinnerOverlay loading={loading} />
      <div>
        <div className='mt-4 flex flex-col'>
          <div className='flex items-center justify-between gap-4'>
            <Title>テナントユーザー基本情報</Title>
            {tenantUserShowResponse && (
              <div className='flex items-center gap-6'>
                {tenantUserShowResponse.can_resend_invitation && (
                  <Button
                    variant='primary'
                    disabled={resendInvitationDisabled}
                    onClick={handleClickResendInvitation}
                  >
                    招待メールを再送する
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
        {formValues && (
          <div className='mx-auto max-w-[800px] py-[60px]'>
            <form
              className='mt-10 flex flex-col gap-[26px]'
              onSubmit={(e) => {
                e.preventDefault();
              }}
            >
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>氏名</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['tenant_user.family_name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='山田'
                      value={formValues.familyName}
                      onChange={(e) => setFormValues({ ...formValues, familyName: e.target.value })}
                      error={hasError(errors?.tenant_user.family_name)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant_user.family_name)}</ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['tenant_user.first_name'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='太郎'
                      value={formValues.firstName}
                      onChange={(e) => setFormValues({ ...formValues, firstName: e.target.value })}
                      error={hasError(errors?.tenant_user.first_name)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant_user.first_name)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>氏名（フリガナ）</label>
                  <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                    必須
                  </div>
                </div>
                <div className='flex gap-4'>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['tenant_user.family_name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='ヤマダ'
                      value={formValues.familyNameKana}
                      onChange={(e) =>
                        setFormValues({ ...formValues, familyNameKana: e.target.value })
                      }
                      error={hasError(errors?.tenant_user.family_name_kana)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant_user.family_name_kana)}</ErrorMessage>
                  </div>
                  <div
                    className='flex w-full flex-col'
                    ref={(el) => (fieldRefs.current['tenant_user.first_name_kana'] = el)}
                  >
                    <Input
                      size='m'
                      type='text'
                      placeholder='タロウ'
                      value={formValues.firstNameKana}
                      onChange={(e) =>
                        setFormValues({ ...formValues, firstNameKana: e.target.value })
                      }
                      error={hasError(errors?.tenant_user.first_name_kana)}
                    />
                    <ErrorMessage>{firstError(errors?.tenant_user.first_name_kana)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex items-center gap-3'>
                  <label className=''>メールアドレス</label>
                </div>
                <div className='flex gap-3'>
                  <label className=''>{tenantUserShowResponse?.email}</label>
                  <a
                    className='cursor-pointer text-primary-500 underline'
                    onClick={handleClickSuggest}
                  >
                    変更提案
                  </a>
                </div>
              </div>
              <div className='flex flex-col gap-[6px]'>
                <div className='flex gap-4'>
                  <div className='flex w-full flex-col gap-[6px]'>
                    <div className='flex items-center gap-3'>
                      <label className=''>権限</label>
                      <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                        必須
                      </div>
                      <div
                        ref={helpIconRef}
                        onMouseOver={handleMouseOver}
                        onMouseLeave={handleMouseLeave}
                      >
                        <img
                          className=''
                          src={ToolTipIcon}
                          alt=''
                          width='15'
                          height='15'
                        />
                        <TenantRoleTooltip
                          active={isTooltipActive}
                          x={tooltipPosition.x}
                          y={tooltipPosition.y}
                        />
                      </div>
                    </div>
                    {roleSelector && (
                      <div
                        ref={(el) => (fieldRefs.current['tenant_user.role_id'] = el)}
                        className='flex w-full flex-col'
                      >
                        <Select
                          items={roleSelector}
                          size='m'
                          initialValue={formValues.roleId}
                          onSelect={(value) => {
                            setFormValues({ ...formValues, roleId: value });
                          }}
                          error={hasError(errors?.tenant_user.role_id)}
                        />
                      </div>
                    )}
                    <ErrorMessage>{firstError(errors?.tenant_user.role_id)}</ErrorMessage>
                  </div>
                  <div className='flex w-full flex-col gap-[6px]'>
                    <div className='flex items-center gap-3'>
                      <label className=''>ステータス</label>
                      <div className='grid w-7 place-items-center bg-warning-500 text-[10px] text-white'>
                        必須
                      </div>
                    </div>
                    {statusSelector && (
                      <div
                        ref={(el) => (fieldRefs.current['tenant_user.enabled'] = el)}
                        className='flex w-full flex-col'
                      >
                        <Select
                          items={statusSelector}
                          size='m'
                          initialValue={formValues.enabled}
                          onSelect={(value) => setFormValues({ ...formValues, enabled: value })}
                          error={hasError(errors?.tenant_user.enabled)}
                        />
                      </div>
                    )}
                    <ErrorMessage>{firstError(errors?.tenant_user.enabled)}</ErrorMessage>
                  </div>
                </div>
              </div>
              <div className='mt-auto flex justify-center gap-5 pt-4'>
                <div className='w-[172px]'>
                  <Button
                    variant='secondary'
                    fullRounded
                    fullWidth
                    outline
                    onClick={handleClickCancel}
                  >
                    キャンセル
                  </Button>
                </div>
                <div className='w-[172px]'>
                  <Button
                    fullRounded
                    fullWidth
                    outline
                    onClick={submit}
                  >
                    保存する
                  </Button>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
      {isOpenSuggestModal && (
        <SuggestEmailUpdateModal
          values={suggestValues}
          onChange={handleSuggestValuesChange}
          onSuggest={onSuggest}
          onClose={handleCancelSuggest}
          isOpen={isOpenSuggestModal}
          errors={suggestErrors}
        />
      )}
    </main>
  );
};

export default TenantUserEditForm;
